/** @jsxImportSource @emotion/react */
import tw from 'twin.macro';
import { ReactComponent as CopySvg } from 'assets/Icons/copy.svg';
import { ReactComponent as ExpandMoreSvg } from 'assets/Icons/expand_more.svg';
import { Hint } from 'components/shared/Hint';
import { ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useCopyToClipboard } from 'react-use';
import { isEmpty } from 'lodash';
import { RootState } from 'reducers';
import { customToast } from 'utils/toast.util';
import { formatBytes, msToSec, secToTime } from '../../MediaUtilities';
import { toFormatTz } from 'utils/date.util';
import { MediaProvider } from 'utils/enum';
import { IMetadata } from 'utils/models';
import { Routes } from 'utils/routes';

type Props = {
  metadata?: IMetadata;
  provider?: MediaProvider;
  ingestedAt?: string | Date;
};

const NOT_AVAILABLE = 'Not available';

/** insert_to_MediaMetaData (api-v2) defaults many unset fields to the literal
 * string "NULL" — treat that (any case) the same as empty/missing. */
const isBlankValue = (value?: string | number | null): boolean => {
  if (value === undefined || value === null) return true;
  const str = String(value).trim();
  return str === '' || str.toUpperCase() === 'NULL';
};

const PROVIDER_LABELS: Partial<Record<MediaProvider, string>> = {
  [MediaProvider.OMNY]: 'Omny Studio',
  [MediaProvider.SIMPLECAST]: 'Simplecast',
  [MediaProvider.MEGAPHONE]: 'Megaphone',
  [MediaProvider.RSS]: 'RSS',
};

const formatDuration = (rawSeconds?: string | null): string | undefined => {
  if (isBlankValue(rawSeconds)) return undefined;
  const seconds = Number(rawSeconds);
  return Number.isFinite(seconds) ? secToTime(seconds) : undefined;
};

const formatFileSize = (rawBytes?: string | null): string | undefined => {
  if (isBlankValue(rawBytes)) return undefined;
  const bytes = Number(rawBytes);
  return Number.isFinite(bytes) ? formatBytes(bytes) : undefined;
};

/** First non-blank value, in priority order — used to fall back to the
 * app's generic (non-CMS) media fields for providers without webhook_body
 * (LOCAL/RSS), so the Media group isn't just "Not available" for those. */
const firstAvailable = (
  ...values: Array<string | null | undefined>
): string | undefined => values.find((value) => !isBlankValue(value)) ?? undefined;

/** metadata.length is milliseconds (see MediaUtilities.msToSec usage across
 * the player); metadata.cmsDurationSeconds (from CMS webhook data) is seconds. */
const formatDurationWithFallback = (
  cmsDurationSeconds?: string | null,
  genericLengthMs?: string | null,
): string | undefined => {
  const cms = formatDuration(cmsDurationSeconds);
  if (cms) return cms;
  if (isBlankValue(genericLengthMs)) return undefined;
  return secToTime(msToSec(genericLengthMs));
};

type PublishedDate = { local: string; utc: string };

const getTimezoneAbbr = (rawDate: string, timeZone: string): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(new Date(rawDate));
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? timeZone;
  } catch {
    return timeZone;
  }
};

/** `timeZone` is the user's own configured Location (Preferences > User
 * Profile > Location — state.library.location), not the browser/OS zone:
 * these dates should follow the account's region setting so they read the
 * same way regardless of which device/browser the user opens them from. */
const formatPublishedDate = (
  rawDate: string | null | undefined,
  timeZone: string,
): PublishedDate | undefined => {
  if (isBlankValue(rawDate)) return undefined;
  try {
    const tzAbbr = getTimezoneAbbr(rawDate as string, timeZone);
    return {
      local: `${toFormatTz(rawDate as string, 'd MMM yyyy, h:mm a', timeZone)} ${tzAbbr}`,
      utc: toFormatTz(rawDate as string, 'd MMM, HH:mm', 'UTC'),
    };
  } catch {
    return undefined;
  }
};

const formatIngestedDate = (
  rawDate: string | null | undefined,
  timeZone: string,
): string | undefined => {
  if (isBlankValue(rawDate)) return undefined;
  try {
    return toFormatTz(rawDate as string, 'd MMM yyyy, h:mm a', timeZone);
  } catch {
    return undefined;
  }
};

/** "https://traffic.omny.fm/d/clips/<uuid>/.../0600.mp3" -> "traffic.omny.fm/…/0600.mp3" */
const shortenUrl = (url: string, maxLength = 40): string => {
  try {
    const parsed = new URL(url);
    const display = `${parsed.hostname}${parsed.pathname}`;
    if (display.length <= maxLength) return display;
    const segments = parsed.pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1] ?? '';
    return `${parsed.hostname}/…/${last}`;
  } catch {
    return url;
  }
};

const LinkValue = ({ url }: { url: string }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    tw="block truncate color[#5551FF] hover:underline"
    title={url}
  >
    {shortenUrl(url)}
  </a>
);

const CopyableValue = ({ value }: { value: string }) => {
  const [, copyToClipboard] = useCopyToClipboard();

  const handleCopy = () => {
    copyToClipboard(value);
    customToast.success('Copied to clipboard');
  };

  return (
    <div tw="flex items-center justify-between gap-x-2 min-width[0] overflow-hidden">
      <span tw="truncate whitespace-nowrap min-width[0]" title={value}>
        {value}
      </span>
      <Hint text="Copy" enterDelay={200} leaveDelay={0}>
        <CopySvg
          tw="cursor-pointer flex-shrink-0 opacity-60 hover:opacity-100"
          width={16}
          height={16}
          onClick={handleCopy}
        />
      </Hint>
    </div>
  );
};

type CollectionEntry = { id: string; name: string };

/** Collection Name — clickable, React Router Link to its Library view
 * (reusing the navigation pattern InsightsTab/shared/AdvertisingTopShows.tsx
 * already established); opens in a new tab so the player/transcript
 * underneath isn't navigated away from. Ticket 637. */
const CollectionNameCell = ({ id, name }: CollectionEntry) => (
  <Link
    to={`${Routes.COLLECTIONS}/${id}`}
    target="_blank"
    rel="noopener noreferrer"
    tw="truncate color[#5551FF] hover:underline min-width[0]"
    title={name}
  >
    {name}
  </Link>
);

/** Collection ID — copyable, kept in its own cell so it lines up in a
 * consistent column under/next to every other collection's ID rather than
 * trailing right after however long that row's name happens to be. */
const CollectionIdCell = ({ id }: { id: string }) => {
  const [, copyToClipboard] = useCopyToClipboard();

  const handleCopy = () => {
    copyToClipboard(id);
    customToast.success('Copied to clipboard');
  };

  return (
    <span tw="flex items-center gap-x-1 min-width[0]">
      <span
        tw="truncate whitespace-nowrap font-family[monospace] font-size[1.1rem] color[#B0B1BE]"
        title={id}
      >
        {id}
      </span>
      <Hint text="Copy" enterDelay={200} leaveDelay={0}>
        <CopySvg
          tw="cursor-pointer flex-shrink-0 opacity-60 hover:opacity-100"
          width={14}
          height={14}
          onClick={handleCopy}
        />
      </Hint>
    </span>
  );
};

/** All eligible collections as one compact list — a single Media field row
 * regardless of how many collections the media belongs to (so "many
 * collections" stays short, not 2 rows each), one collection per line.
 *
 * Name and ID are split into their own grid columns (not just spaced apart
 * within one row) so every collection's ID lines up under the others
 * instead of trailing at a different position depending on how long that
 * row's name happens to be. */
const CollectionsValue = ({ collections }: { collections: CollectionEntry[] }) => (
  <div
    css={tw`grid gap-x-6 gap-y-1.5`}
    style={{ gridTemplateColumns: 'minmax(0, 1fr) auto' }}
  >
    {collections.flatMap((collection) => [
      <CollectionNameCell key={`${collection.id}-name`} {...collection} />,
      <CollectionIdCell key={`${collection.id}-id`} id={collection.id} />,
    ])}
  </div>
);

type FieldRow = {
  label: string;
  fieldKey: string;
  value?: string | null;
  isLink?: boolean;
  isCopyable?: boolean;
  /** Custom rendering (e.g. publish-state dot) — overrides value/isLink/isCopyable. */
  render?: ReactNode;
};

const FieldValue = (field: FieldRow) => {
  if (field.render !== undefined) {
    return (
      <div tw="min-width[0] truncate">
        {field.render}
      </div>
    );
  }

  if (isBlankValue(field.value)) {
    return <span tw="italic color[#8B8D9B] whitespace-nowrap">{NOT_AVAILABLE}</span>;
  }

  if (field.isLink) return <LinkValue url={field.value as string} />;
  if (field.isCopyable) return <CopyableValue value={field.value as string} />;

  return (
    <span tw="block truncate whitespace-nowrap" title={String(field.value)}>
      {field.value}
    </span>
  );
};

const FieldRows = ({ fields }: { fields: FieldRow[] }) => (
  <>
    {fields.map((field, index) => (
      <div
        // Index-qualified: repeated labels (e.g. one "Collection" row per
        // collection membership) are valid and must not collide as keys.
        key={`${field.label}-${index}`}
        tw="flex items-start gap-x-6 py-3 border-bottom[1px solid #EEEEF2]"
      >
        <div tw="width[16rem] flex-shrink-0">
          <div tw="font-size[1.5rem] font-medium">{field.label}</div>
          <div tw="font-size[1.2rem] color[#B0B1BE] font-family[monospace]">
            {field.fieldKey}
          </div>
        </div>
        <div tw="flex-1 font-size[1.5rem] color[#333] min-width[0]">
          <FieldValue {...field} />
        </div>
      </div>
    ))}
  </>
);

const HeaderRule = () => (
  <div tw="height[1px] background-color[#D8D9E0] rounded-full w-full" aria-hidden />
);

const groupHeaderTitleStyles = tw`uppercase font-size[1.2rem] font-medium color[#8B8D9B] tracking-wide`;

const GroupHeader = ({
  title,
  isFirst = false,
}: {
  title: string;
  isFirst?: boolean;
}) => (
  <div css={[tw`flex flex-col gap-y-2`, !isFirst && tw`mt-6`]}>
    <HeaderRule />
    <div css={groupHeaderTitleStyles}>{title}</div>
    <HeaderRule />
  </div>
);

const CollapsibleGroupHeader = ({
  title,
  expanded,
  onToggle,
  isFirst = false,
}: {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  isFirst?: boolean;
}) => (
  <div css={[tw`flex flex-col gap-y-2`, !isFirst && tw`mt-6`]}>
    <HeaderRule />
    <button
      type="button"
      onClick={onToggle}
      tw="flex items-center gap-x-1 bg-transparent border-0 p-0 cursor-pointer hover:color[#54566C]"
      css={groupHeaderTitleStyles}
      aria-expanded={expanded}
    >
      <ExpandMoreSvg
        tw="flex-shrink-0 transition-transform duration-200"
        css={[!expanded && tw`-rotate-90`]}
        width={16}
        height={16}
      />
      {title}
    </button>
    <HeaderRule />
  </div>
);

export const EpisodeInfoPanel = ({ metadata, provider, ingestedAt }: Props) => {
  const [isMediaExpanded, setIsMediaExpanded] = useState(true);

  // Ticket 637 — Collection group. The backend only returns raw membership
  // (metadata.collectionIds); eligibility is decided here by cross-checking
  // against the Collection UI's own already-computed visible-collection
  // state (tenant + user-group permission + personal "visible_collections"
  // preference, per collection.slice.ts setVisibleCollection), plus the
  // same `!isArchived` filter CollectionSidebar/FilterBar apply — so this
  // panel can never show a collection the Collection UI itself would hide,
  // and never re-implements that filtering logic.
  //
  // No personal "visible_collections" preference configured means no
  // restriction, not "hide everything" — mirrors CollectionSidebar's
  // `!isEmpty(visibleCollection) ? visibleCollection : items` fallback, so
  // this panel doesn't show fewer collections than the sidebar would.
  const visibleCollection = useSelector(
    (state: RootState) => state.collection.visibleCollection,
  );
  const collectionItems = useSelector(
    (state: RootState) => state.collection.items,
  );

  const eligibleCollections: CollectionEntry[] = useMemo(() => {
    const memberOfIds = metadata?.collectionIds;
    if (!memberOfIds || memberOfIds.length === 0) return [];
    const memberOfIdSet = new Set(memberOfIds);

    const eligibleSource = isEmpty(visibleCollection)
      ? collectionItems
      : visibleCollection;

    return eligibleSource
      .filter((collection) => !collection.isArchived && memberOfIdSet.has(collection.id))
      .map((collection) => ({ id: collection.id, name: collection.name }));
  }, [metadata?.collectionIds, visibleCollection, collectionItems]);

  // The account's configured region (Preferences > User Profile > Location),
  // not the browser/OS timezone — so these dates read the same regardless
  // of which device the user opens them from. state.library.location is
  // mirrored from state.userProfile.location at profile load (see
  // sagas/profileSaga.ts) and already used the same way for date formatting
  // elsewhere (Player.tsx autosave-toast times); falls back to the
  // browser's zone only if that hasn't loaded yet (should be rare — it
  // defaults to 'Australia/Sydney', see reducers/library.ts).
  const userLocation = useSelector((state: RootState) => state.library.location);
  const timeZone = userLocation || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const providerLabel = provider ? PROVIDER_LABELS[provider] : undefined;
  const ingested = formatIngestedDate(
    ingestedAt ? String(ingestedAt) : undefined,
    timeZone,
  );
  const publishedDate = formatPublishedDate(metadata?.publishedUtc, timeZone);

  const isPublished =
    !isBlankValue(metadata?.publishState) &&
    metadata?.publishState?.toLowerCase() === 'published';

  /** Local/device uploads and RSS items have no CMS record backing the
   * Identifiers/Show/Publishing groups, so only Media is meaningful — show
   * it alone, at the top. CMS-ingested providers keep the full layout. */
  const isMediaOnlyProvider =
    provider === MediaProvider.LOCAL || provider === MediaProvider.RSS;

  const hasCollections = eligibleCollections.length > 0;

  // Collection membership lives inside the Media group (Ticket 637 UI
  // pass): it's media-level information ("where does this episode live"),
  // not CMS-episode metadata, and folding it in here — rather than a
  // separate group — keeps the panel short when a media belongs to several
  // collections (one compact field, not two rows per collection). It's
  // also independent of provider/CMS ingestion (a locally uploaded or RSS
  // episode can belong to a collection same as any other), which is why it
  // has to be part of Media specifically — the one group both layout
  // branches below share — rather than Identifiers/Show/Publishing, which
  // only exist for CMS-ingested providers.
  const mediaSection = (
    <>
      <CollapsibleGroupHeader
        title="Media"
        expanded={isMediaExpanded}
        onToggle={() => setIsMediaExpanded((expanded) => !expanded)}
        isFirst={isMediaOnlyProvider}
      />
      {isMediaExpanded && (
        <FieldRows
          fields={[
            ...(hasCollections
              ? [
                  {
                    label: eligibleCollections.length > 1 ? 'Collections' : 'Collection',
                    fieldKey: 'CollectionIds',
                    render: <CollectionsValue collections={eligibleCollections} />,
                  },
                ]
              : []),
            {
              label: 'Duration',
              fieldKey: 'DurationSeconds',
              value: formatDurationWithFallback(
                metadata?.cmsDurationSeconds,
                metadata?.length,
              ),
            },
            {
              label: 'File size',
              fieldKey: 'PublishedAudioSizeInBytes',
              value:
                formatFileSize(metadata?.cmsAudioFileSizeBytes) ??
                formatFileSize(metadata?.size),
            },
            {
              label: 'Source audio',
              fieldKey: 'AudioUrl',
              value: firstAvailable(metadata?.cmsAudioUrl, metadata?.audiourl),
              isLink: true,
            },
            {
              label: 'Thumbnail',
              fieldKey: 'ImageUrl',
              value: firstAvailable(metadata?.cmsThumbnailUrl, metadata?.thumbnail),
              isLink: true,
            },
          ]}
        />
      )}
    </>
  );

  return (
    <div tw="py-5 px-4 overflow-y-auto h-full">
      <div tw="flex flex-nowrap justify-between items-baseline mb-4 gap-x-4 min-width[0]">
        <div tw="font-size[1.8rem] font-medium color[#333] flex-shrink-0">Info</div>
        {(providerLabel || ingested) && (
          <div tw="font-size[1.3rem] color[#8B8D9B] text-right whitespace-nowrap truncate min-width[0]">
            {providerLabel && (
              <>
                Source:{' '}
                <span tw="font-medium color[#54566C]">{providerLabel}</span>
              </>
            )}
            {providerLabel && ingested && ' · '}
            {ingested && (
              <>
                Ingested: <span tw="font-medium color[#54566C]">{ingested}</span>
              </>
            )}
          </div>
        )}
      </div>

      {isMediaOnlyProvider ? (
        <div tw="min-width[0] overflow-hidden">{mediaSection}</div>
      ) : (
        <div tw="flex flex-nowrap gap-x-12 items-start min-width[0]">
          <div tw="flex-1 min-width[0] overflow-hidden">
            <GroupHeader title="Identifiers" isFirst />
            <FieldRows
              fields={[
                {
                  label: 'Episode ID / Clip ID',
                  fieldKey: 'Id',
                  value: metadata?.clipid,
                  isCopyable: true,
                },
                {
                  label: 'Program ID',
                  fieldKey: 'ProgramId',
                  value: metadata?.programid,
                  isCopyable: true,
                },
                {
                  label: 'External ID',
                  fieldKey: 'ExternalId',
                  value: metadata?.externalId,
                  isCopyable: true,
                },
              ]}
            />

            <GroupHeader title="Show" />
            <FieldRows
              fields={[
                { label: 'Show', fieldKey: 'program_Name', value: metadata?.programname },
                { label: 'Network', fieldKey: 'program_Network', value: metadata?.network },
                {
                  label: 'Organization',
                  fieldKey: 'organization_name',
                  value: metadata?.organizationName,
                },
                {
                  label: 'Category',
                  fieldKey: 'program_Category',
                  value: metadata?.programCategory,
                },
              ]}
            />
          </div>

          <div tw="flex-1 min-width[0] flex flex-col min-h-full overflow-hidden">
            <GroupHeader title="Publishing" isFirst />
            <FieldRows
              fields={[
                {
                  label: 'Publish state',
                  fieldKey: 'PublishState',
                  value: metadata?.publishState,
                  render: isBlankValue(metadata?.publishState) ? undefined : (
                    <span tw="flex items-center gap-x-2 whitespace-nowrap">
                      {isPublished && (
                        <span tw="width[0.8rem] height[0.8rem] rounded-full background-color[#2ECC71] display[inline-block]" />
                      )}
                      {metadata?.publishState}
                    </span>
                  ),
                },
                { label: 'Visibility', fieldKey: 'Visibility', value: metadata?.visibility },
                {
                  label: 'Published date',
                  fieldKey: 'PublishedUtc',
                  value: metadata?.publishedUtc,
                  render: publishedDate ? (
                    <span tw="whitespace-nowrap truncate block" title={`${publishedDate.local} (${publishedDate.utc} UTC)`}>
                      {publishedDate.local}{' '}
                      <span tw="color[#B0B1BE]">({publishedDate.utc} UTC)</span>
                    </span>
                  ) : undefined,
                },
                {
                  label: 'Source URL',
                  fieldKey: 'PublishedUrl',
                  value: metadata?.cmsSourceUrl,
                  isLink: true,
                },
                {
                  label: 'Content rating',
                  fieldKey: 'ContentRating',
                  value: metadata?.contentRating,
                },
                { label: 'Episode type', fieldKey: 'EpisodeType', value: metadata?.episodeType },
                {
                  label: 'Season / Episode',
                  fieldKey: 'Season / Episode',
                  value:
                    !isBlankValue(metadata?.season) || !isBlankValue(metadata?.episode)
                      ? `${!isBlankValue(metadata?.season) ? `S${metadata?.season}` : '—'} • ${
                          !isBlankValue(metadata?.episode) ? `E${metadata?.episode}` : '—'
                        }`
                      : undefined,
                },
              ]}
            />

            {mediaSection}
          </div>
        </div>
      )}

      <div tw="font-size[1.3rem] color[#8B8D9B] mt-6 leading-relaxed">
        Read-only. Values reflect the CMS record at time of ingest and are not
        written back. Info appears in the toolbar for CMS-ingested and
        manually uploaded episodes; fields with no source data show "Not
        available".
      </div>
    </div>
  );
};
